import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCircleCheck,
  IconClock,
  IconInfoCircle,
} from '@tabler/icons';
import { cn } from '#root/lib/utils';
import styles from './notice.module.scss';

const noticeVariants = cva(styles.notice, {
  variants: {
    tone: {
      info: styles.info,
      success: styles.success,
      warning: styles.warning,
      error: styles.error,
      one: styles.one,
    },
    strip: {
      true: styles.strip,
      false: styles.box,
    },
  },
  defaultVariants: {
    tone: 'info',
    strip: false,
  },
});

const toneIcons = {
  info: IconInfoCircle,
  success: IconCircleCheck,
  warning: IconAlertTriangle,
  error: IconAlertCircle,
  one: IconClock,
};

export interface NoticeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof noticeVariants> {
  /** Ícono a la izquierda; por defecto, el del tono. `null` lo oculta */
  icon?: React.ReactNode;
  /** Acción a la derecha (ej. un `Button variant="link"`); en angosto baja a su propia línea */
  action?: React.ReactNode;
}

/**
 * Notice Component - Design System
 *
 * Aviso de estado: ícono + texto + acción opcional, con el tono como color.
 * `strip` lo pinta de borde a borde dentro de un `Card` (franja bajo el contenido); sin él es
 * una caja redondeada. Varias líneas: un `<p>` por línea.
 *
 * @example
 * <Notice tone="one" strip>Asignando tu pedido al mejor viajero disponible.</Notice>
 *
 * @example
 * <Notice action={<Button variant="link" size="sm">Revisar</Button>}>
 *   <p>Esperando la aprobación de Guvery.</p>
 *   <p>¿Necesitas agilizar la entrega?</p>
 * </Notice>
 */
const Notice = React.forwardRef<HTMLDivElement, NoticeProps>(
  ({ className, tone, strip, icon, action, children, role = 'status', ...props }, ref) => {
    const ToneIcon = toneIcons[tone ?? 'info'];
    const iconNode = icon === undefined ? <ToneIcon size={20} /> : icon;

    return (
      <div
        ref={ref}
        role={role}
        className={cn(noticeVariants({ tone, strip }), className)}
        {...props}>
        {iconNode && (
          <span className={styles.icon} aria-hidden="true">
            {iconNode}
          </span>
        )}
        <div className={styles.body}>{children}</div>
        {action && <div className={styles.action}>{action}</div>}
      </div>
    );
  }
);

Notice.displayName = 'Notice';

export { Notice, noticeVariants };
